import { Block, EMAIL_MARKER, NoteRow, SummaryRow } from "./types";

// The map-link token. Stored as a plain string so the literal "${mapLink}"
// is emitted (not interpolated). A value that an older build corrupted by
// writing the rendered "Get Directions" button HTML back into the field is
// healed back to this clean token on serialize.
const MAP_LINK_TOKEN = "${mapLink}";
function normalizeValue(value?: string): string {
	const s = String(value ?? "");
	if (s.includes(MAP_LINK_TOKEN)) return MAP_LINK_TOKEN;
	if (s.includes("Get Directions") && s.includes("inline-block")) return MAP_LINK_TOKEN;
	return s;
}

function serializeBlock(b: Block): string {
	switch (b.type) {
		case "header": {
			const out: string[] = [];
			if (b.data.eyebrow) out.push(`Eyebrow: ${b.data.eyebrow}`);
			if (b.data.title) out.push(`Title: ${b.data.title}`);
			if (b.data.subtitle) out.push(`Subtitle: ${b.data.subtitle}`);
			// Emit each non-empty header line as "Line: <text>"
			(b.data.lines || []).forEach((l) => {
				if (l.trim()) out.push(`Line: ${l}`);
			});
			return out.join("\n");
		}
		case "paragraph":
			return (b.data.html || "").trim();
		case "summary": {
			const rows = (b.data.rows || []) as SummaryRow[];
			const lines = rows.map((r) => {
				// Raw passthrough row (e.g. "# Section Header") — emit verbatim.
				if (r.raw !== undefined) return r.raw;
				// Pair-column row: emit "LeftLabel: leftValue | RightLabel: rightValue"
				if (r.label2 !== undefined && r.label2 !== "") {
					const left = `${r.label}${r.emphasis ? "!" : ""}: ${normalizeValue(r.value)}`;
					const right = `${r.label2}${r.emphasis2 ? "!" : ""}: ${normalizeValue(r.value2)}`;
					return `${left} | ${right}`;
				}
				return `${r.label}${r.emphasis ? "!" : ""}: ${normalizeValue(r.value)}`;
			});
			return `[Summary]\n${lines.join("\n")}\n[/Summary]`;
		}
		case "notes": {
			const noteTitle = b.data.title || "";
			let inner: string;
			if (b.style?.layout === "rows" && b.data.rows) {
				inner = (b.data.rows as NoteRow[]).map((r) => `${r.k} ${r.v}`).join("\n");
			} else {
				inner = (b.data.items || []).map((t) => `- ${t}`).join("\n");
			}
			return `[Notes: ${noteTitle}]\n${inner}\n[/Notes]`;
		}
		case "footer": {
			// Fully verbatim: all content lives in data.lines[], emitted in order.
			// data.title / data.note are NOT used — they would reorder lines and break round-trips.
			const out: string[] = ["[Footer]"];
			(b.data.lines || []).forEach((l) => out.push(l));
			out.push("[/Footer]");
			return out.join("\n");
		}
		default:
			// chip / button / divider have no representation in the current [[WL-EMAIL]] grammar
			// (Phase 2/3 will extend grammar + renderer). Throw loud to prevent silent data loss.
			throw new Error(`serialize: block type "${(b as Block).type}" is not supported in [[WL-EMAIL]] markup yet`);
	}
}

export function serialize(blocks: Block[]): string {
	const body = blocks
		.map(serializeBlock)
		.filter((s) => s.length > 0)
		.join("\n\n");
	return `${EMAIL_MARKER}\n${body}`;
}
